import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import "react-accessible-accordion/dist/fancy-example.css";
import styles from "../../StaffAttendanceTable.module.scss";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import { withTranslation } from "react-i18next";
import { Portal } from "react-overlays";
import OfficeFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/OfficeFilter";
import EmployeesFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/EmployeesFilter";
import { useStaffDesignation } from "repositories/scheduler-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import Loader from "components/Loader";

const StaffAttendanceSidebar = ({
  t,
  dateRange,
  updateDateRange,
  holidays,
  selectedOfficeIds,
  setSelectedOfficeIds,
  selectedUserIds,
  selectedDesignationIds,
  selectedStaffStatus,
  selectedTimesheetStatuses,
  ownerId,
  onApplyFilters,
}) => {
  const [isSidebarActive, setIsSidebarActive] = useState(true);
  const { selectedId } = useAccountOwnerCommon();
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Local state for filters that apply on "Apply" button click
  // Note: EmployeesFilter uses staff 'id', but we need to convert to 'userId' for API
  const [localSelectedStaffIds, setLocalSelectedStaffIds] = useState([]);
  const [staffIdToUserIdMap, setStaffIdToUserIdMap] = useState(new Map());
  const [localSelectedDesignationIds, setLocalSelectedDesignationIds] =
    useState(selectedDesignationIds);
  const [localSelectedStaffStatus, setLocalSelectedStaffStatus] = useState(
    Array.isArray(selectedStaffStatus)
      ? selectedStaffStatus
      : selectedStaffStatus?.value
      ? [selectedStaffStatus.value]
      : ["IsActiveInclude"]
  );
  const [localSelectedTimesheetStatuses, setLocalSelectedTimesheetStatuses] =
    useState(selectedTimesheetStatuses);
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);

  // Convert user IDs to staff IDs when syncing from props
  useEffect(() => {
    // When selectedUserIds change and we have the mapping, convert to staff IDs
    // Support one-to-many: same user can be in multiple offices (multiple staffIds for same userId)
    if (selectedUserIds.length > 0 && staffIdToUserIdMap.size > 0) {
      // Create a one-to-many mapping: userId -> array of staffIds
      const userIdToStaffIdsMap = new Map();
      staffIdToUserIdMap.forEach((userId, staffId) => {
        if (!userIdToStaffIdsMap.has(userId)) {
          userIdToStaffIdsMap.set(userId, []);
        }
        userIdToStaffIdsMap.get(userId).push(staffId);
      });

      // Get ALL staff IDs for each selected userId
      const staffIds = [];
      selectedUserIds.forEach((userId) => {
        const staffIdsForUser = userIdToStaffIdsMap.get(userId) || [];
        staffIds.push(...staffIdsForUser);
      });

      setLocalSelectedStaffIds(staffIds);
    } else if (selectedUserIds.length === 0) {
      setLocalSelectedStaffIds([]);
    }
  }, [selectedUserIds, staffIdToUserIdMap]);

  useEffect(() => {
    setLocalSelectedDesignationIds(selectedDesignationIds);
  }, [selectedDesignationIds]);

  useEffect(() => {
    setLocalSelectedStaffStatus(
      Array.isArray(selectedStaffStatus)
        ? selectedStaffStatus
        : selectedStaffStatus?.value
        ? [selectedStaffStatus.value]
        : ["IsActiveInclude"]
    );
  }, [selectedStaffStatus]);

  useEffect(() => {
    setLocalSelectedTimesheetStatuses(selectedTimesheetStatuses);
  }, [selectedTimesheetStatuses]);

  const handleSidebarToggle = () => {
    setIsSidebarActive((prev) => !prev);
  };

  const handleResetFilter = () => {
    setSelectedOfficeIds([]);
    setLocalSelectedStaffIds([]);
    setLocalSelectedDesignationIds([]);
    setLocalSelectedStaffStatus(["IsActiveInclude"]);
    setLocalSelectedTimesheetStatuses([]);
    setSelectedRoles([]);
    // Trigger search reset in EmployeesFilter
    setResetSearchTrigger((prev) => prev + 1);

    // Apply the reset immediately
    if (onApplyFilters) {
      onApplyFilters({
        userIds: [],
        designationIds: [],
        staffStatus: ["IsActiveInclude"],
        timesheetStatuses: [],
      });
    }
  };

  // Convert staff IDs to user IDs using the mapping
  // Deduplicate userIds since multiple staff entries can have the same userId
  const convertStaffIdsToUserIds = (staffIds) => {
    const userIds = staffIds
      .map((staffId) => staffIdToUserIdMap.get(staffId))
      .filter(Boolean);
    // Remove duplicates while preserving order
    return [...new Set(userIds)];
  };

  const handleApplyFilters = () => {
    if (onApplyFilters) {
      // Convert staff IDs to user IDs
      const userIds = convertStaffIdsToUserIds(localSelectedStaffIds);
      onApplyFilters({
        userIds,
        designationIds: localSelectedDesignationIds,
        staffStatus: localSelectedStaffStatus, // Now an array
        timesheetStatuses: localSelectedTimesheetStatuses,
      });
    }
  };

  const [errors, setErrors] = useState({
    from: "",
    to: "",
  });

  // Clear errors when date range changes (auto-adjustment from parent)
  useEffect(() => {
    setErrors({ from: "", to: "" });
  }, [dateRange]);

  const updateDatesValues = (date, type) => {
    if (!date || !dateRange || !updateDateRange) return;

    const today = moment().startOf("day");
    const selectedDate = moment(date).startOf("day");

    if (type === "from") {
      const selectedFromDate = selectedDate;

      // Don't allow future dates
      if (selectedFromDate.isAfter(today)) {
        setErrors((prev) => ({
          ...prev,
          from: "From date cannot be in the future",
        }));
        return;
      }

      // Clear errors - parent will handle auto-adjustment
      setErrors({ from: "", to: "" });
    } else if (type === "to") {
      const selectedToDate = selectedDate;
      const fromDate = moment(dateRange.from).startOf("day");

      // Don't allow future dates
      if (selectedToDate.isAfter(today)) {
        setErrors((prev) => ({
          ...prev,
          to: "To date cannot be in the future",
        }));
        return;
      }

      if (selectedToDate.isBefore(fromDate)) {
        setErrors((prev) => ({
          ...prev,
          to: "To date cannot be before From date",
        }));
        return;
      }

      // Clear errors - parent will handle auto-adjustment
      setErrors({ from: "", to: "" });
    }

    updateDateRange(date, type);
  };

  const CalendarContainer = ({ children }) => {
    const el = document.getElementById("calendar-portal");

    return <Portal container={el}>{children}</Portal>;
  };

  // Get designations for filter - keep category structure
  const { isLoading: loadingRoleData, data: staffRoleData } =
    useStaffDesignation({ selectedId });

  // Keep the category structure for designation filter
  const designationCategories = useMemo(() => {
    if (!loadingRoleData && staffRoleData?.length) {
      return staffRoleData || [];
    }
    return [];
  }, [loadingRoleData, staffRoleData]);

  // Handle designation selection (local state only, applies on Apply button)
  const handleDesignationChange = (designationId, checked) => {
    if (checked) {
      setLocalSelectedDesignationIds((prev) => {
        if (!prev.includes(designationId)) {
          return [...prev, designationId];
        }
        return prev;
      });
    } else {
      setLocalSelectedDesignationIds((prev) =>
        prev.filter((id) => id !== designationId)
      );
    }
  };

  // Timesheet status filter options
  const timesheetStatusOptions = [
    { label: "Pending for Approval", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Paid", value: "paid" },
    { label: "Mixed", value: "mixed" },
  ];

  // Staff status filter options
  const staffStatusOptions = [
    { label: "Active", value: "IsActiveInclude" },
    { label: "Inactive", value: "IsInActiveInclude" },
    { label: "Deleted", value: "IsDeleteInclude" },
  ];

  // Handle timesheet status change (local state only, applies on Apply button)
  const handleTimesheetStatusChange = (value) => {
    setLocalSelectedTimesheetStatuses((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  // Handle staff status change (local state only, applies on Apply button)
  const handleStaffStatusChange = (value) => {
    setLocalSelectedStaffStatus((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  // Handle office filter change - clear staff when offices change (applies immediately)
  const handleOfficeFilterChange = (offices) => {
    setSelectedOfficeIds(offices);
    // Clear staff selection when offices change
    setLocalSelectedStaffIds([]);
  };

  return (
    <div className="scheduler-tabs-main-wrapper m-0">
      <StickySidebar
        isSidebarActive={isSidebarActive}
        handleSidebarToggle={handleSidebarToggle}
        resetFilter={handleResetFilter}
      >
        <Accordion
          allowMultipleExpanded
          allowZeroExpanded
          preExpanded={["a", "b", "c"]}
          className="filter-accordion"
        >
          <AccordionItem uuid="a">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.dateRange")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="invitation_date_picker">
                <div className="invitation_date">
                  <div className="c-field mb-3">
                    <label>{t("from")}</label>
                    <div className="d-flex inputdate">
                      <ReactDatePickerCommon
                        dateFormat="dd/MM/yyyy"
                        className="c-form-control"
                        selected={dateRange?.from || null}
                        openToDate={dateRange?.from || moment().toDate()}
                        onChange={(e) => updateDatesValues(e, "from")}
                        maxDate={
                          dateRange?.to
                            ? moment(dateRange.to).endOf("day").toDate()
                            : moment().endOf("day").toDate()
                        }
                        popperPlacement="bottom-start"
                        popperContainer={CalendarContainer}
                      />
                      {errors.from && (
                        <span
                          className="error-msg"
                          style={{ display: "inline" }}
                        >
                          {errors.from}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="invitation_date">
                  <div className="c-field mb-3">
                    <label>{t("to")}</label>
                    <div className="d-flex inputdate">
                      <ReactDatePickerCommon
                        dateFormat="dd/MM/yyyy"
                        className="c-form-control"
                        selected={dateRange?.to || null}
                        openToDate={dateRange?.to || moment().toDate()}
                        minDate={dateRange?.from || null}
                        maxDate={moment().endOf("day").toDate()}
                        onChange={(e) => updateDatesValues(e, "to")}
                        popperPlacement="bottom-end"
                        popperContainer={CalendarContainer}
                      />
                      {errors.to && (
                        <span
                          className="error-msg"
                          style={{ display: "inline" }}
                        >
                          {errors.to}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Display holidays in the selected date range */}
              {(() => {
                if (
                  !dateRange?.from ||
                  !dateRange?.to ||
                  !holidays ||
                  holidays.size === 0
                )
                  return null;

                const fromDate = moment(dateRange.from);
                const toDate = moment(dateRange.to);
                const holidaysInRange = [];

                // Iterate over the Map
                holidays.forEach((holiday, dateStr) => {
                  const holidayDate = moment(dateStr);
                  if (holidayDate.isBetween(fromDate, toDate, "day", "[]")) {
                    holidaysInRange.push({
                      ...holiday,
                      date: dateStr,
                    });
                  }
                });

                if (holidaysInRange.length === 0) return null;

                return (
                  <div className={styles.warning_style}>
                    <p className={styles.warning_text}>
                      <span>{t("staffAttendance.holidaysInThisPeriod")}</span>{" "}
                      {holidaysInRange.map((holiday, index) => (
                        <span key={holiday.date}>
                          <strong>
                            {holiday.title} (
                            {moment(holiday.date).format("MMM DD, YYYY")})
                          </strong>
                          {index < holidaysInRange.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </p>
                  </div>
                );
              })()}
            </AccordionItemPanel>
          </AccordionItem>

          <AccordionItem uuid="b" className="accordion__item border_none">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.offices")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <OfficeFilter
                setOfficeFilter={handleOfficeFilterChange}
                selectedOffice={selectedOfficeIds}
                selectedOwnerId={ownerId}
                includeOwnerOffices={true}
                allElement={(officeList) => {
                  if (!officeList || officeList.length === 0) return null;
                  const allSelected =
                    selectedOfficeIds.length === officeList.length;
                  return (
                    <ul>
                      <li>
                        <div
                          className={
                            styles.checkbox_border + " " + "ch-checkbox mb-1"
                          }
                        >
                          <label className="mb-3">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleOfficeFilterChange(
                                    officeList.map((office) => office.id)
                                  );
                                } else {
                                  handleOfficeFilterChange([]);
                                }
                              }}
                              checked={allSelected}
                            />
                            <span>{t("staffAttendance.selectAll")}</span>
                          </label>
                        </div>
                      </li>
                    </ul>
                  );
                }}
              />
            </AccordionItemPanel>
          </AccordionItem>

          {/* Staff Members Filter */}
          <AccordionItem uuid="c">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.staffMembers")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <EmployeesFilter
                t={t}
                selectedEmpFilter={localSelectedStaffIds}
                setEmpFilter={(staffIds) => {
                  setLocalSelectedStaffIds(staffIds);
                }}
                ownerId={ownerId}
                selectedOfficeFilter={selectedOfficeIds}
                selectedRoles={selectedRoles}
                setSelectedRoles={setSelectedRoles}
                ownerData={null}
                hideOwner={true}
                showProfilePic={true}
                deduplicateByUserId={true}
                hideRoleFilter={true}
                resetSearchTrigger={resetSearchTrigger}
                setUsersList={(staffList) => {
                  // Build mapping from staff ID to user ID
                  const newMap = new Map();
                  staffList?.forEach((staff) => {
                    if (staff?.id && staff?.userId) {
                      newMap.set(staff.id, staff.userId);
                    }
                  });
                  setStaffIdToUserIdMap(newMap);

                  // Filter out any selected staff IDs that are no longer in the list
                  // This preserves valid selections when the staff list changes
                  setLocalSelectedStaffIds((prev) => {
                    return prev.filter((staffId) => newMap.has(staffId));
                  });
                }}
              />
            </AccordionItemPanel>
          </AccordionItem>

          {/* Designation Filter - Categories in Accordion */}
          <AccordionItem uuid="e">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.staffRoles")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="accordion_panel_height">
              <ul className={["filter-list"]}>
                {loadingRoleData && <Loader />}
                {designationCategories.length > 0 && (
                  <Accordion
                    allowZeroExpanded
                    preExpanded={
                      designationCategories.length > 0
                        ? [designationCategories[0]?.id?.toString()]
                        : []
                    }
                    className="border-0"
                  >
                    {designationCategories.map((category) => (
                      <AccordionItem
                        key={category.id}
                        uuid={category.id?.toString()}
                        className="border-0"
                      >
                        <AccordionItemHeading>
                          <AccordionItemButton>
                            {category.name}
                          </AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel>
                          {category.designations &&
                            category.designations.length > 0 &&
                            category.designations.map((designation) => (
                              <div className="ch-checkbox" key={designation.id}>
                                <label className="mb-2">
                                  <input
                                    type="checkbox"
                                    checked={localSelectedDesignationIds.includes(
                                      designation.id
                                    )}
                                    onChange={(e) =>
                                      handleDesignationChange(
                                        designation.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <span>{designation.name}</span>
                                </label>
                              </div>
                            ))}
                        </AccordionItemPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </ul>
            </AccordionItemPanel>
          </AccordionItem>

          {/* Timesheet Status Filter (FE only) */}
          <AccordionItem uuid="d">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.timesheetStatus")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <ul className={["filter-list"]}>
                {timesheetStatusOptions.map((option) => (
                  <div className="ch-checkbox" key={option.value}>
                    <label className="mb-2">
                      <input
                        type="checkbox"
                        checked={localSelectedTimesheetStatuses.includes(
                          option.value
                        )}
                        onChange={() =>
                          handleTimesheetStatusChange(option.value)
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  </div>
                ))}
              </ul>
            </AccordionItemPanel>
          </AccordionItem>

          {/* Staff Status Filter - Checkboxes (Multi-select) */}
          <AccordionItem uuid="f">
            <AccordionItemHeading>
              <AccordionItemButton>
                {t("staffAttendance.staffStatus")}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <ul className="filter-list">
                {staffStatusOptions.map((option) => (
                  <li key={option.value}>
                    <div className="ch-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={localSelectedStaffStatus.includes(
                            option.value
                          )}
                          onChange={() => handleStaffStatusChange(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>

        <div className={styles.btn_container}>
          <button
            className="button button-round button-shadow"
            onClick={handleApplyFilters}
          >
            {t("apply")}
          </button>
          <button
            className="button button-round button-border button-dark"
            onClick={handleResetFilter}
          >
            {t("reset")}
          </button>
        </div>
      </StickySidebar>
    </div>
  );
};

export default withTranslation()(StaffAttendanceSidebar);
